/**
 * Which paycheck pays which bill, worked out LIVE from due days and pay days.
 *
 * ⚠️ NOT STORED, and that is the whole design. `personal_budget_bills` already
 * has a `paycheck_id`, it is NOT NULL, and it is the column RLS traverses to
 * reach a user_id (bills → paychecks → budgets). So every bill is physically
 * attached to one paycheck whether or not that attachment means anything — and
 * today it does not: the setup flow puts every bill it creates on the FIRST
 * paycheck.
 *
 * That column stays what it is, an ownership anchor. This module decides what
 * she SEES. The two will routinely disagree, which is expected: a stored
 * assignment would have to be rewritten every time she edited a due day or a
 * pay day, and the version on screen would be stale for however long that
 * took.
 *
 * All pure, no React, no Supabase.
 *
 * In `_shared/` because the app's budget sheet and `kin-chat`'s `get_budget`
 * both have to answer "which paycheck covers this bill", and a second copy of
 * this rule is how the screen and Kin start giving different answers about the
 * same month. `src/lib/billPaycheckMatch.ts` is a re-export shim.
 */

import {
  DUE_DAY_LAST,
  LAST_DAY,
  lastDayOfMonth,
  payDayFromStored,
  type PayDay,
} from "./payDay.ts";

/** Re-exported so callers of the matcher need only one import. */
export { DUE_DAY_LAST };

export interface MatchablePaycheck {
  id: string;
  name: string;
  amount: number | string;
  /** A `date`, or null for the many rows written before setup saved one. */
  pay_date: string | null;
  sort_order: number;
}

export interface MatchableBill {
  id: string;
  paycheck_id: string;
  name: string;
  amount: number | string;
  /** 1-31, 32 for the last day, or null for "no due date yet". */
  due_day: number | null;
}

export interface PaycheckGroup<
  P extends MatchablePaycheck = MatchablePaycheck,
  B extends MatchableBill = MatchableBill,
> {
  paycheck: P;
  bills: B[];
  /** Sum of the bills MATCHED here, not of everything pointing at the row. */
  billTotal: number;
  /** The paycheck minus only its matched bills. */
  leftover: number;
}

export interface BillMatch<
  P extends MatchablePaycheck = MatchablePaycheck,
  B extends MatchableBill = MatchableBill,
> {
  groups: PaycheckGroup<P, B>[];
  /** Bills with no due day. They belong to no paycheck, by design. */
  noDueDate: B[];
  /**
   * True when NO paycheck has a pay_date, so there is nothing to match
   * against. Callers show the "add your pay day" nudge and fall back to one
   * flat list rather than inventing pay dates.
   */
  payDaysUnknown: boolean;
}

/**
 * The day of the month a `due_day` means, resolved against a real month.
 *
 * 32 becomes that month's actual last day, so February behaves. A day the
 * month is too short for clamps down for the same reason — the 31st in a
 * 30-day month is the 30th, not "never".
 */
export function resolveDueDay(dueDay: number | null, monthISO: string): number | null {
  if (dueDay === null || !Number.isFinite(dueDay)) return null;
  const last = lastDayOfMonth(monthISO);
  // ⚠️ This branch is REDUNDANT with the clamp below and a mutation run
  // confirmed it: `Math.min(32, last)` is `last` for every month, so deleting
  // this line changes no result and fails no test. Kept, in the same spirit as
  // rowOrder's unreachable branches in tripTimeline.ts, because it states the
  // sentinel's MEANING at the one place that knows it — a reader should not
  // have to derive "32 means the last day" from an arithmetic coincidence.
  if (dueDay >= DUE_DAY_LAST) return last;
  if (dueDay < 1) return null;
  return Math.min(Math.trunc(dueDay), last);
}

/** A paycheck's pay day as a day number in `monthISO`, or null if unset. */
export function resolvePayDay(
  paycheck: Pick<MatchablePaycheck, "pay_date">,
  monthISO: string,
): number | null {
  const stored: PayDay | null = payDayFromStored(paycheck.pay_date);
  if (stored === null) return null;
  const last = lastDayOfMonth(monthISO);
  if (stored === LAST_DAY) return last;
  return Math.min(Math.max(1, stored), last);
}

const num = (v: number | string): number => {
  const n = typeof v === "number" ? v : Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Group bills under the paycheck that covers them.
 *
 * THE RULE: a bill goes to the paycheck whose pay day is the LATEST one on or
 * before the bill's due day. Money has to be in the account before the bill
 * comes out, so the relevant paycheck is the most recent one to have arrived.
 *
 * ⚠️ THE WRAPAROUND. A bill due BEFORE the month's first pay day has no
 * earlier paycheck in this month, and it is not unfunded — it is paid by the
 * last paycheck of the previous cycle, which is the same slot as the LAST
 * paycheck here. So "due the 3rd" with pay days on the 5th and the 20th lands
 * on the 20th, not the 5th. Dropping it on the 5th would overstate what the
 * 5th has to cover and understate the 20th's, every month.
 *
 * With one paycheck everything lands on it, which falls out of the rule rather
 * than being special-cased.
 */
export function matchBillsToPaychecks<
  P extends MatchablePaycheck,
  B extends MatchableBill,
>(paychecks: P[], bills: B[], monthISO: string): BillMatch<P, B> {
  const ordered = [...paychecks].sort((a, b) => a.sort_order - b.sort_order);

  const noDueDate = bills.filter((b) => resolveDueDay(b.due_day, monthISO) === null);
  const dated = bills.filter((b) => resolveDueDay(b.due_day, monthISO) !== null);

  // Pay days, paired with their paycheck, earliest first. A paycheck with no
  // pay_date cannot anchor anything and is skipped here — it still gets a
  // group, just an empty one.
  const anchors = ordered
    .map((p) => ({ paycheck: p, day: resolvePayDay(p, monthISO) }))
    .filter((a): a is { paycheck: P; day: number } => a.day !== null)
    .sort((a, b) => a.day - b.day);

  const payDaysUnknown = anchors.length === 0;

  const assigned = new Map<string, B[]>();
  for (const p of ordered) assigned.set(p.id, []);

  if (!payDaysUnknown) {
    for (const bill of dated) {
      const due = resolveDueDay(bill.due_day, monthISO) as number;
      // The latest anchor at or before the due day.
      let chosen = anchors.filter((a) => a.day <= due).pop();
      // None: the bill falls before the first pay day of the month, so the
      // previous cycle's final paycheck covers it — the last anchor.
      if (!chosen) chosen = anchors[anchors.length - 1];
      assigned.get(chosen.paycheck.id)?.push(bill);
    }
  }

  const groups: PaycheckGroup<P, B>[] = ordered.map((paycheck) => {
    const own = (assigned.get(paycheck.id) ?? []).slice().sort(dueOrder<B>(monthISO));
    const billTotal = own.reduce((s, b) => s + num(b.amount), 0);
    return {
      paycheck,
      bills: own,
      billTotal,
      leftover: num(paycheck.amount) - billTotal,
    };
  });

  return {
    groups,
    noDueDate: noDueDate.slice().sort((a, b) => a.name.localeCompare(b.name)),
    payDaysUnknown,
  };
}

/** Earliest due day first, then by name so the order is stable. */
function dueOrder<B extends MatchableBill>(monthISO: string) {
  return (a: B, b: B): number => {
    const da = resolveDueDay(a.due_day, monthISO) ?? 99;
    const db = resolveDueDay(b.due_day, monthISO) ?? 99;
    if (da !== db) return da - db;
    return a.name.localeCompare(b.name);
  };
}

/**
 * When no pay day is known anywhere: one flat list, due date order.
 *
 * Deliberately NOT a guess at pay dates. Inventing "probably the 1st and the
 * 15th" would put her bills under paychecks that may not pay them and give
 * every card a confident, wrong leftover.
 */
export function billsByDueDate<B extends MatchableBill>(
  bills: B[],
  monthISO: string,
): B[] {
  return bills.slice().sort(dueOrder<B>(monthISO));
}

/** One bill owed before the next paycheck, with the month it falls in. */
export interface DueBeforePayday<B extends MatchableBill = MatchableBill> {
  bill: B;
  /** Its resolved day of the month. */
  due: number;
  /**
   * It falls in the month AFTER the one being read.
   *
   * ⚠️ Load-bearing for the caller's wording, not a convenience. In a wrapped
   * window "due the 3rd" alongside "due the last day" reads as a date that has
   * already passed and is out of order, when it is actually next month's. The
   * caller has to be able to say which.
   */
  nextMonth: boolean;
}

/** The next pay day, and what she owes before it arrives. */
export interface NextPaydayView<B extends MatchableBill = MatchableBill> {
  /** Day of the month the next paycheck lands, or null when no pay day is saved. */
  nextPayDay: number | null;
  /**
   * The next pay day falls in the FOLLOWING month, so the window wraps.
   *
   * True whenever every saved pay day has already passed this month, which is
   * the normal state for the last stretch of any month.
   */
  wrapsToNextMonth: boolean;
  /** Bills owed from today until that pay day, soonest first. */
  dueBefore: DueBeforePayday<B>[];
}

/**
 * What is still owed before the next paycheck lands.
 *
 * ⚠️ A LIST, NEVER A BALANCE, and that distinction is the whole reason this
 * function exists. The obvious-looking alternative is a per-paycheck leftover
 * (`amount - its bills`), and that number is unsayable: it ignores spending
 * transactions and bills with no due day, so it sums HIGHER than the month's
 * real leftover and is wrong in the direction that makes her spend money she
 * does not have. A list of real obligations carries no such claim.
 *
 * THE WINDOW is [today, nextPayDay). Bills due TODAY are included: an unpaid
 * bill omitted is the costly error, and a bill she already paid this morning
 * being mentioned is not.
 *
 * ⚠️ IT WRAPS. On the 25th with pay days on the 5th and 20th, the next paycheck
 * is next month's 5th, so the window is "the 25th to the end of this month"
 * PLUS "the 1st to the 4th". Due days repeat every month, so those early bills
 * are the same rows read again rather than a second query. Without the wrap,
 * the last third of every month would report nothing owed, which is the exact
 * stretch where she most needs to know.
 */
export function billsDueBeforeNextPayday<P extends MatchablePaycheck, B extends MatchableBill>(
  paychecks: P[],
  bills: B[],
  monthISO: string,
  todayDay: number,
): NextPaydayView<B> {
  const anchors = paychecks
    .map((p) => resolvePayDay(p, monthISO))
    .filter((d): d is number => d !== null)
    .sort((a, b) => a - b);

  // No pay day saved anywhere: there is no "next payday" to measure against,
  // and inventing one would put a confident deadline on her bills.
  if (anchors.length === 0) {
    return { nextPayDay: null, wrapsToNextMonth: false, dueBefore: [] };
  }

  const ahead = anchors.filter((d) => d > todayDay);
  const wrapsToNextMonth = ahead.length === 0;
  // ⚠️ `d > todayDay`, strictly. A paycheck landing TODAY has already arrived,
  // so the next one is the following pay day, not this morning's.
  const nextPayDay = wrapsToNextMonth ? anchors[0] : ahead[0];

  const dated = bills
    .map((b) => ({ bill: b, due: resolveDueDay(b.due_day, monthISO) }))
    .filter((x): x is { bill: B; due: number } => x.due !== null);

  const inWindow = dated.filter(({ due }) =>
    wrapsToNextMonth
      // Rest of this month, or early next month before the pay day. These
      // cannot overlap: every anchor has passed, so nextPayDay <= todayDay.
      ? due >= todayDay || due < nextPayDay
      : due >= todayDay && due < nextPayDay,
  );

  // Next month's bills sort AFTER this month's. Ordering on the bare due day
  // would put next month's 2nd ahead of this month's 28th.
  const last = lastDayOfMonth(monthISO);
  const key = (due: number) => (wrapsToNextMonth && due < todayDay ? due + last : due);

  return {
    nextPayDay,
    wrapsToNextMonth,
    dueBefore: inWindow
      .slice()
      .sort((a, b) => key(a.due) - key(b.due) || a.bill.name.localeCompare(b.bill.name))
      .map(({ bill, due }) => ({ bill, due, nextMonth: wrapsToNextMonth && due < todayDay })),
  };
}
